/**
 * PartyRSVP · HandleLife End-of-Summer Party
 * ==========================================
 * Route: /rsvp/handlelife-party
 *
 * Flow:
 *   1. Public teaser (title, host, city only, running potluck count)
 *   2. RSVP form (parent name/email/phone, headcount, items, notes)
 *   3. On submit → address + full potluck list revealed
 *   4. If ?token=XXXX in URL → jumps to revealed state and enables edits
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { PartyPopper, MapPin, Users, Plus, X, Loader2, ExternalLink, CheckCircle2, Utensils, Calendar } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const api = axios.create({ baseURL: `${API_BASE}/api` });

export default function PartyRSVP() {
  const [params] = useSearchParams();
  const urlToken = (params.get('token') || '').trim().toUpperCase();

  const [teaser, setTeaser] = useState(null);
  const [party, setParty] = useState(null);
  const [myRsvp, setMyRsvp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [form, setForm] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    headcount: 4, items: [], notes: '',
  });
  const [itemDraft, setItemDraft] = useState('');

  const refreshTeaser = async () => {
    try {
      const r = await api.get('/academy/rsvp/handlelife-party/preview');
      setTeaser(r.data);
    } catch {/* ignore */}
  };

  useEffect(() => {
    (async () => {
      await refreshTeaser();
      if (urlToken) {
        try {
          setLoading(true);
          const r = await api.get(`/academy/rsvp/handlelife-party/${urlToken}`);
          setParty(r.data.party);
          setMyRsvp(r.data.my_rsvp);
          setForm(f => ({
            ...f,
            parent_name: r.data.my_rsvp.parent_name || '',
            parent_email: r.data.my_rsvp.parent_email || '',
            parent_phone: r.data.my_rsvp.parent_phone || '',
            headcount: r.data.my_rsvp.headcount || 4,
            items: r.data.my_rsvp.items || [],
            notes: r.data.my_rsvp.notes || '',
          }));
        } catch {
          toast.error('Could not load your RSVP — please re-submit.');
        } finally { setLoading(false); }
      }
    })();
  }, [urlToken]);

  const addItem = (val) => {
    const t = (val || itemDraft || '').trim();
    if (!t) return;
    if (form.items.some(i => i.toLowerCase() === t.toLowerCase())) return;
    setForm(f => ({ ...f, items: [...f.items, t] }));
    setItemDraft('');
  };

  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.parent_name.trim() || !form.parent_email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/academy/rsvp/handlelife-party', form);
      setParty(r.data.party);
      setMyRsvp({ ...form });
      await refreshTeaser();
      // Update URL with token so bookmark works
      const url = new URL(window.location.href);
      url.searchParams.set('token', r.data.token);
      window.history.replaceState({}, '', url.toString());
      toast.success('RSVP confirmed! Address unlocked below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RSVP failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const updateRsvp = async () => {
    if (!urlToken && !myRsvp) return;
    setSubmitting(true);
    try {
      const token = urlToken || (new URLSearchParams(window.location.search).get('token') || '').toUpperCase();
      await api.patch(`/academy/rsvp/handlelife-party/${token}`, {
        headcount: form.headcount, items: form.items, notes: form.notes,
      });
      setMyRsvp({ ...form });
      await refreshTeaser();
      toast.success('RSVP updated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed.');
    } finally { setSubmitting(false); }
  };

  const isConfirmed = Boolean(party && myRsvp);

  return (
    <div data-testid="party-rsvp-page" className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-amber-50 py-10 px-4">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="rounded-2xl bg-slate-900 text-white p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-violet-300">
            <PartyPopper className="w-3.5 h-3.5" /> You're invited
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black leading-tight">HandleLife End-of-Summer Party</h1>
          <div className="mt-2 text-slate-300 text-sm">HandleLife × Scholar Athlete Academy · Hosted by The Singletary Family</div>
          {(party?.when_display || teaser?.when_display) && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-500/20 border border-amber-400/60 px-4 py-2.5 text-sm font-bold text-amber-200" data-testid="party-when">
              <Calendar className="w-4 h-4 text-amber-300" /> {party?.when_display || teaser?.when_display}
            </div>
          )}
          {(party?.rain_check_note || teaser?.rain_check_note) && (
            <div className="mt-2 text-xs text-amber-200/80 italic">
              {party?.rain_check_note || teaser?.rain_check_note}
            </div>
          )}
          {!isConfirmed && teaser && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-200">
              <MapPin className="w-4 h-4 text-amber-400" /> {teaser.teaser_location}
            </div>
          )}
          {teaser && (
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-300">
              <span><strong className="text-white">{teaser.total_rsvps}</strong> families RSVPed</span>
              <span><strong className="text-white">{teaser.total_guests}</strong> guests total</span>
            </div>
          )}
        </div>

        {/* Address reveal card */}
        {isConfirmed && party && (
          <div data-testid="party-address-card" className="mt-6 rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-6 shadow-lg">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-emerald-700">
              <CheckCircle2 className="w-4 h-4" /> Address unlocked
            </div>
            <div className="mt-3 text-2xl font-black text-slate-900">
              {party.address_line1}
            </div>
            <div className="text-lg text-slate-700">
              {party.address_city}, {party.address_state} {party.address_zip}
            </div>
            <a href={party.map_url} target="_blank" rel="noreferrer"
               className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-bold"
               data-testid="party-map-link">
              Open in Google Maps <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Form */}
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6 shadow-lg">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-slate-500">
            <Users className="w-4 h-4" /> {isConfirmed ? 'Your RSVP · Edit Anytime' : 'RSVP to reveal the address'}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Parent Name *</label>
              <input data-testid="rsvp-name-input" type="text" value={form.parent_name} disabled={isConfirmed}
                     onChange={e => setForm({ ...form, parent_name: e.target.value })}
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-600" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Email *</label>
              <input data-testid="rsvp-email-input" type="email" value={form.parent_email} disabled={isConfirmed}
                     onChange={e => setForm({ ...form, parent_email: e.target.value })}
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-600" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Phone (optional)</label>
              <input data-testid="rsvp-phone-input" type="tel" value={form.parent_phone}
                     onChange={e => setForm({ ...form, parent_phone: e.target.value })}
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">How Many Attending *</label>
              <input data-testid="rsvp-headcount-input" type="number" min={1} max={25} value={form.headcount}
                     onChange={e => setForm({ ...form, headcount: Math.max(1, parseInt(e.target.value || '1')) })}
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
              <Utensils className="inline w-3.5 h-3.5 mr-1" /> What Are You Bringing
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.items.map((it, i) => (
                <span key={i} data-testid={`rsvp-item-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 text-violet-800 px-3 py-1.5 text-sm font-semibold">
                  {it}
                  <button onClick={() => removeItem(i)} className="hover:bg-violet-200 rounded-full p-0.5"
                          data-testid={`rsvp-item-remove-${i}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input data-testid="rsvp-item-input" type="text" value={itemDraft} placeholder="Add a dish, drink, or supply…"
                     onChange={e => setItemDraft(e.target.value)}
                     onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
                     className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <button data-testid="rsvp-item-add-btn" onClick={() => addItem()}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-bold hover:bg-slate-800">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Bring anything you'd like — food, drinks, dessert, ice, cups, whatever your family enjoys. Check "What's Being Brought" below to avoid duplicates.
            </p>
          </div>

          <div className="mt-5">
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Notes (allergies, arrival time, etc.)</label>
            <textarea data-testid="rsvp-notes-input" rows={2} value={form.notes}
                      onChange={e => setForm({ ...form, notes: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          <div className="mt-6">
            {!isConfirmed ? (
              <button data-testid="rsvp-submit-btn" onClick={submit} disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 text-base font-black uppercase tracking-wide disabled:opacity-60">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <PartyPopper className="w-5 h-5" />}
                {submitting ? 'Submitting…' : 'RSVP + Reveal Address'}
              </button>
            ) : (
              <button data-testid="rsvp-update-btn" onClick={updateRsvp} disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 text-base font-black uppercase tracking-wide disabled:opacity-60">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Update My RSVP'}
              </button>
            )}
          </div>
        </div>

        {/* Who's going + what's being brought — visible BEFORE and AFTER RSVP.
            Attribution intentionally hidden: user requested "just that it's being brought". */}
        {(teaser?.attendees?.length > 0 || teaser?.bringing?.length > 0) && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-lg" data-testid="party-attendees-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-slate-500">
                  <Users className="w-4 h-4" /> Who's Going
                </div>
                <div className="text-xs text-slate-500 font-semibold">
                  {teaser?.total_rsvps || 0} {(teaser?.total_rsvps || 0) === 1 ? 'family' : 'families'} · {teaser?.total_guests || 0} guests
                </div>
              </div>
              <ul className="mt-4 divide-y divide-slate-100">
                {(teaser?.attendees || []).length === 0 && (
                  <li className="text-sm text-slate-500 py-3 italic">No RSVPs yet. Be the first!</li>
                )}
                {(teaser?.attendees || []).map((a, i) => (
                  <li key={i} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="font-bold text-slate-900 truncate">{a.family}</span>
                    <span className="ml-3 text-xs text-slate-500 shrink-0">{a.headcount} {a.headcount === 1 ? 'guest' : 'guests'}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-lg" data-testid="party-bringing-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-black text-slate-500">
                  <Utensils className="w-4 h-4" /> What's Being Brought
                </div>
                <div className="text-xs text-slate-500 font-semibold">{(teaser?.bringing || []).length} items</div>
              </div>
              {(teaser?.bringing || []).length === 0 ? (
                <p className="mt-4 text-sm text-slate-500 italic">Nothing yet — add your dish, drink, or side above.</p>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {(teaser.bringing || []).map((it, i) => (
                    <span key={i} className="inline-block rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-semibold">
                      {it}
                    </span>
                  ))}
                </div>
              )}
              <p className="mt-4 text-[11px] text-slate-400 italic">Names hidden on purpose — just the running list of what's coming.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
